import { useState, useEffect, useCallback } from 'react';
import { Building2, Receipt, Landmark, Hash, Loader2, Save, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { getBillingSettings, updateBillingSettings } from '@/services/apiClient';
import { toast } from 'sonner';

/**
 * Seller identity and tax configuration.
 *
 * Nothing on this page is ever pre-filled with a plausible-looking value. An
 * invented GSTIN on a real invoice is a legal problem, so the page reports what
 * is missing and invoicing stays blocked until a human fills it in.
 */

function Field({ label, hint, children, className = '' }) {
    return (
        <div className={className}>
            <label className="text-sm font-medium text-foreground">{label}</label>
            <div className="mt-1.5">{children}</div>
            {hint && <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">{hint}</p>}
        </div>
    );
}

function Section({ icon: Icon, title, description, children }) {
    return (
        <div className="rounded-xl border border-border bg-card p-6">
            <div className="mb-5 flex items-start gap-3">
                <div className="mt-0.5 rounded-lg border border-border bg-muted/40 p-2">
                    <Icon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                    <h2 className="font-semibold text-foreground">{title}</h2>
                    {description && <p className="text-sm text-muted-foreground">{description}</p>}
                </div>
            </div>
            <div className="space-y-4">{children}</div>
        </div>
    );
}

export default function SuperAdminBillingSettingsPage() {
    const [form, setForm] = useState(null);
    const [missing, setMissing] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        try {
            const res = await getBillingSettings();
            setForm(res.data.settings);
            setMissing(res.data.missingForInvoice || []);
        } catch (err) {
            toast.error(err.message || 'Could not load billing settings.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const update = (field, value) => setForm(f => ({ ...f, [field]: value }));

    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await updateBillingSettings(form);
            setForm(res.data.settings);
            setMissing(res.data.missingForInvoice || []);
            toast.success('Billing settings saved.');
        } catch (err) {
            toast.error(err.message || 'Could not save.');
        } finally {
            setSaving(false);
        }
    };

    if (loading || !form) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
        );
    }

    const ready = missing.length === 0;

    return (
        <div className="space-y-6 pb-10">
            <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-foreground">Billing Settings</h1>
                    <p className="text-sm text-muted-foreground">
                        Your identity as it appears on every invoice you issue.
                    </p>
                </div>
                <Button onClick={handleSave} disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save settings
                </Button>
            </div>

            {/* Readiness — stated plainly, because invoicing is blocked until it clears */}
            <div className={`flex items-start gap-3 rounded-xl border p-4 ${
                ready
                    ? 'border-emerald-500/40 bg-emerald-500/5'
                    : 'border-amber-500/40 bg-amber-500/5'
            }`}>
                {ready
                    ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600 dark:text-emerald-500" />
                    : <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600 dark:text-amber-500" />}
                <div className="text-sm">
                    {ready ? (
                        <p className="font-medium text-foreground">Ready to issue invoices.</p>
                    ) : (
                        <>
                            <p className="font-medium text-foreground">Invoicing is blocked.</p>
                            <p className="mt-0.5 text-muted-foreground">
                                Still needed: <strong className="text-foreground">{missing.join(', ')}</strong>.
                                Nothing is guessed here — an invented GSTIN on a real invoice is a legal problem.
                            </p>
                        </>
                    )}
                </div>
            </div>

            <Section
                icon={Building2}
                title="Seller identity"
                description="Printed as the 'from' party on every invoice."
            >
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Registered legal name">
                        <Input value={form.legalName} onChange={e => update('legalName', e.target.value)}
                            placeholder="Velnex Technologies Private Limited" />
                    </Field>
                    <Field label="Trading name" hint="Shown as the heading. Falls back to the legal name.">
                        <Input value={form.tradeName} onChange={e => update('tradeName', e.target.value)}
                            placeholder="EchoHire" />
                    </Field>
                    <Field label="GSTIN" hint="The first two digits set your state and decide CGST+SGST vs IGST.">
                        <Input value={form.gstin} onChange={e => update('gstin', e.target.value.toUpperCase())}
                            placeholder="27AAPFU0939F1ZV" maxLength={15} className="font-mono text-sm" />
                    </Field>
                    <Field label="PAN">
                        <Input value={form.pan} onChange={e => update('pan', e.target.value.toUpperCase())}
                            placeholder="AAPFU0939F" maxLength={10} className="font-mono text-sm" />
                    </Field>
                </div>

                <Field label="Registered address">
                    <Input value={form.addressLine1} onChange={e => update('addressLine1', e.target.value)}
                        placeholder="Address line 1" />
                    <Input value={form.addressLine2} onChange={e => update('addressLine2', e.target.value)}
                        placeholder="Address line 2 (optional)" className="mt-2" />
                </Field>

                <div className="grid gap-4 sm:grid-cols-4">
                    <Field label="City">
                        <Input value={form.city} onChange={e => update('city', e.target.value)} />
                    </Field>
                    <Field label="State">
                        <Input value={form.state} onChange={e => update('state', e.target.value)} />
                    </Field>
                    <Field label="State code" hint="Derived from your GSTIN.">
                        <Input value={form.stateCode} readOnly className="font-mono text-sm bg-muted/40" />
                    </Field>
                    <Field label="Pincode">
                        <Input value={form.pincode} onChange={e => update('pincode', e.target.value)} />
                    </Field>
                </div>

                <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Contact email">
                        <Input type="email" value={form.contactEmail} onChange={e => update('contactEmail', e.target.value)} />
                    </Field>
                    <Field label="Contact phone">
                        <Input value={form.contactPhone} onChange={e => update('contactPhone', e.target.value)} />
                    </Field>
                    <Field label="Website">
                        <Input value={form.website} onChange={e => update('website', e.target.value)} />
                    </Field>
                </div>
            </Section>

            <Section
                icon={Receipt}
                title="Tax"
                description="How GST is applied. The split is decided per customer from their state."
            >
                <label className="flex cursor-pointer items-start gap-3 rounded-lg border border-border bg-muted/20 p-3">
                    <input type="checkbox" checked={form.gstEnabled}
                        onChange={e => update('gstEnabled', e.target.checked)}
                        className="mt-0.5 accent-teal-500" />
                    <span>
                        <span className="block text-sm font-medium text-foreground">Raise GST invoices</span>
                        <span className="mt-0.5 block text-xs text-muted-foreground">
                            Off means every invoice is a plain non-GST document. This is separate from a
                            customer having no GSTIN — an unregistered buyer still pays GST, they just
                            cannot claim credit.
                        </span>
                    </span>
                </label>

                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Combined GST rate (%)"
                        hint="Split half and half into CGST and SGST within your own state.">
                        <Input type="number" min={0} max={100} step="0.01" value={form.gstRate}
                            onChange={e => update('gstRate', Number(e.target.value))} />
                    </Field>
                    <Field label="Default SAC code" hint="998314 is the usual classification for SaaS.">
                        <Input value={form.defaultSacCode} onChange={e => update('defaultSacCode', e.target.value)}
                            className="font-mono text-sm" />
                    </Field>
                </div>
            </Section>

            <Section
                icon={Hash}
                title="Numbering and terms"
                description="Series reset each financial year and are never reused."
            >
                <div className="grid gap-4 sm:grid-cols-3">
                    <Field label="Invoice prefix" hint="Produces EH/26-27/0001.">
                        <Input value={form.invoicePrefix} onChange={e => update('invoicePrefix', e.target.value)}
                            className="font-mono text-sm" />
                    </Field>
                    <Field label="Order prefix">
                        <Input value={form.orderPrefix} onChange={e => update('orderPrefix', e.target.value)}
                            className="font-mono text-sm" />
                    </Field>
                    <Field label="Payment terms (days)" hint="Overridable per order.">
                        <Input type="number" min={0} max={365} value={form.defaultPaymentTermDays}
                            onChange={e => update('defaultPaymentTermDays', Number(e.target.value))} />
                    </Field>
                </div>

                <Field label="Invoice terms" hint="Printed under the totals — payment terms, jurisdiction, late fees.">
                    <textarea
                        value={form.invoiceTerms}
                        onChange={e => update('invoiceTerms', e.target.value)}
                        rows={3}
                        placeholder="Payment due within 15 days. Subject to Pune jurisdiction."
                        className="w-full resize-y rounded-md border border-input bg-background px-3 py-2 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    />
                </Field>
            </Section>

            <Section
                icon={Landmark}
                title="Payment details"
                description="Printed on the invoice so the customer knows where to pay."
            >
                <div className="grid gap-4 sm:grid-cols-2">
                    <Field label="Account name">
                        <Input value={form.bankAccountName} onChange={e => update('bankAccountName', e.target.value)} />
                    </Field>
                    <Field label="Bank name">
                        <Input value={form.bankName} onChange={e => update('bankName', e.target.value)} />
                    </Field>
                    <Field label="Account number">
                        <Input value={form.bankAccountNumber} onChange={e => update('bankAccountNumber', e.target.value)}
                            className="font-mono text-sm" />
                    </Field>
                    <Field label="IFSC">
                        <Input value={form.bankIfsc} onChange={e => update('bankIfsc', e.target.value.toUpperCase())}
                            className="font-mono text-sm" />
                    </Field>
                    <Field label="UPI ID" className="sm:col-span-2">
                        <Input value={form.upiId} onChange={e => update('upiId', e.target.value)} />
                    </Field>
                </div>
            </Section>

            <div className="flex justify-end">
                <Button onClick={handleSave} disabled={saving} className="gap-2">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    Save settings
                </Button>
            </div>
        </div>
    );
}
